import { useMutation } from '@apollo/client';
import { IAddBillingAddressQueryResult, IAddressV2 } from '@monofront/types';
import { ADD_BILLING_ADDRESS } from '../../queries/checkout';
import { useCheckoutCartState } from '../../contexts/CheckoutCartContext';

interface IUseAddBillingAddress {
  addBillingAddress: (omsShoppingCartId: string, address: IAddressV2) => void;
  loading: boolean;
  error: any;
}

/**
 * This hook is meant to add a billing address to a checkout order
 * @returns a hook that is used in the delivery to change the billing address in the oms cart
 */
export const useAddBillingAddress = (): IUseAddBillingAddress => {
  const { checkoutCart } = useCheckoutCartState();
  const [addBillingAddressMutation, { loading, error }] =
    useMutation<IAddBillingAddressQueryResult>(ADD_BILLING_ADDRESS, {
      context: {
        Headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
      },
    });

  const addBillingAddress = (
    omsShoppingCartId: string,
    address: IAddressV2,
  ) => {
    const optimisticResponse = checkoutCart
      ? {
          addBillingAddress: {
            ...checkoutCart,
            billingAddress: {
              ...address,
              __typename: 'AddressCartV2',
            },
            __typename: 'CartV2',
          },
        }
      : undefined;

    return addBillingAddressMutation({
      variables: {
        tokenValue: omsShoppingCartId,
        billingAddress: {
          firstName: address.firstName,
          lastName: address.lastName,
          city: address.city,
          countryCode: address.countryCode,
          postcode: address.postcode,
          street: address.street,
          company: address.company,
          provinceCode: address.provinceCode,
          phoneNumber: address.phoneNumber,
        },
      },
      optimisticResponse,
    });
  };

  return {
    addBillingAddress,
    loading,
    error,
  };
};

export default useAddBillingAddress;
